import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button, Card, Row, Col, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ReportedListingDetails = ({ listing, onEject }) => {
  const [fullDetails, setFullDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ejecting, setEjecting] = useState(false);
  const [ejectModal, setEjectModal] = useState(false);
  const [ejectAction, setEjectAction] = useState(null);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.junctionverse.com';

  useEffect(() => {
    const fetchFullDetails = async () => {
      try {
        const listingId = listing?.listingId || listing?.listing?.id;
        if (!listingId) {
          setLoading(false);
          return;
        }

        const response = await axios.get(
          `${API_BASE_URL}/admin/reported-listings/${listingId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
            },
          }
        );
        setFullDetails(response.data);
      } catch (err) {
        console.error('Failed to fetch listing details:', err);
        toast.error('Failed to fetch listing details');
      } finally {
        setLoading(false);
      }
    };

    fetchFullDetails();
  }, [listing, API_BASE_URL]);

  const handleEject = async (action) => {
    setEjecting(true);
    try {
      const listingId = listing?.listingId || listing?.listing?.id;
      const response = await axios.post(
        `${API_BASE_URL}/admin/reported-listings/${listingId}/eject`,
        { action },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      toast.success(response.data.message || 'Action completed successfully');
      setEjectModal(false);
      setEjectAction(null);
      
      // Refresh the full details to get updated status immediately
      const detailsResponse = await axios.get(
        `${API_BASE_URL}/admin/reported-listings/${listingId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        }
      );
      setFullDetails(detailsResponse.data);
      
      // Call the onEject callback to refresh the list
      if (onEject) {
        setTimeout(() => {
          onEject();
        }, 500);
      }
    } catch (err) {
      console.error('Error ejecting listing:', err);
      toast.error(err?.response?.data?.error || 'Failed to perform action');
    } finally {
      setEjecting(false);
    }
  };

  if (loading) {
    return <p>Loading details...</p>;
  }

  if (!fullDetails) {
    return <p style={{ color: 'red' }}>Failed to load listing details</p>;
  }

  const listingData = fullDetails;
  const images = Array.isArray(listingData.images) ? listingData.images : [];

  return (
    <div>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />

      {/* Listing Information */}
      <Card className="mb-3 p-3">
        <h5 className="mb-3">Listing Information</h5>
        <Row>
          <Col md={6}>
            <p><strong>Title:</strong> {listingData.title}</p>
            <p><strong>Name:</strong> {listingData.name || 'N/A'}</p>
            <p><strong>Category:</strong> {listingData.category}</p>
            {listingData.subCategory && (
              <p><strong>Subcategory:</strong> {listingData.subCategory}</p>
            )}
            <p><strong>Condition:</strong> {listingData.condition}</p>
            {listingData.brand && (
              <p><strong>Brand:</strong> {listingData.brand}</p>
            )}
            {listingData.yearOfPurchase && (
              <p><strong>Year of Purchase:</strong> {listingData.yearOfPurchase}</p>
            )}
            {listingData.usage && (
              <p><strong>Usage:</strong> {listingData.usage}</p>
            )}
            <p><strong>Price:</strong> ${listingData.price || 'N/A'}</p>
            {listingData.soldPrice && (
              <p><strong>Sold Price:</strong> ${listingData.soldPrice}</p>
            )}
            {listingData.isAuction && (
              <p><strong>Type:</strong> <span style={{ color: '#007bff', fontWeight: 'bold' }}>Auction</span></p>
            )}
            {listingData.readableLocation && (
              <p><strong>Location:</strong> {listingData.readableLocation}</p>
            )}
            <p><strong>Listing Status:</strong> 
              <span style={{ 
                marginLeft: '8px',
                padding: '4px 8px',
                borderRadius: '4px',
                backgroundColor: listingData.status === 'For Sale' ? '#28a745' : 
                                listingData.status === 'Removed' ? '#dc3545' : '#6c757d',
                color: 'white',
                fontSize: '12px'
              }}>
                {listingData.status}
              </span>
            </p>
            <p><strong>Report Status:</strong> 
              {(() => {
                const reportStatuses = listingData.reports?.map(r => r.status) || [];
                let overallStatus = 'PENDING';
                if (listingData.status === 'Removed') {
                  overallStatus = 'REMOVED';
                } else if (reportStatuses.every(s => s === 'DISMISSED')) {
                  overallStatus = 'DISMISSED';
                } else if (reportStatuses.every(s => s === 'RESOLVED')) {
                  overallStatus = 'RESOLVED';
                } else if (reportStatuses.includes('PENDING')) {
                  overallStatus = 'PENDING';
                }
                
                const statusConfig = {
                  'PENDING': { bg: '#ffc107', color: '#000', text: 'Pending' },
                  'REMOVED': { bg: '#dc3545', color: '#fff', text: 'Removed' },
                  'DISMISSED': { bg: '#6c757d', color: '#fff', text: 'Dismissed' },
                  'RESOLVED': { bg: '#28a745', color: '#fff', text: 'Resolved' },
                };
                const config = statusConfig[overallStatus] || statusConfig['PENDING'];
                return (
                  <span style={{ 
                    marginLeft: '8px',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    backgroundColor: config.bg,
                    color: config.color,
                    fontSize: '12px',
                    fontWeight: 'bold',
                    textTransform: 'uppercase'
                  }}>
                    {config.text}
                  </span>
                );
              })()}
            </p>
            <p><strong>Created:</strong> {new Date(listingData.createdAt).toLocaleString()}</p>
            {listingData.updatedAt && (
              <p><strong>Last Updated:</strong> {new Date(listingData.updatedAt).toLocaleString()}</p>
            )}
          </Col>
          <Col md={6}>
            <p><strong>Description:</strong></p>
            <p style={{ 
              backgroundColor: '#f5f5f5', 
              padding: '10px', 
              borderRadius: '4px',
              maxHeight: '150px',
              overflowY: 'auto'
            }}>
              {listingData.description}
            </p>
          </Col>
        </Row>

        {/* Images */}
        {images.length > 0 && (
          <div className="mt-3">
            <strong>Images ({images.length}):</strong>
            <div className="d-flex flex-wrap gap-2 mt-2">
              {images.map((img, idx) => (
                <img
                  key={idx}
                  src={img.fileUrl}
                  alt={`${listingData.title} - ${idx + 1}`}
                  style={{
                    width: '100px',
                    height: '100px',
                    objectFit: 'cover',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                    cursor: 'pointer',
                  }}
                  onClick={() => window.open(img.fileUrl, '_blank')}
                />
              ))}
            </div>
          </div>
        )}

        {/* Video */}
        {listingData.video && (
          <div className="mt-3">
            <strong>Video:</strong>
            <div className="mt-2">
              <video
                controls
                style={{
                  maxWidth: '100%',
                  maxHeight: '400px',
                  borderRadius: '4px',
                }}
              >
                <source src={listingData.video} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        )}
      </Card>

      {/* Seller Information */}
      <Card className="mb-3 p-3">
        <h5 className="mb-3">Seller Information</h5>
        <Row>
          <Col md={6}>
            <p><strong>Name:</strong> {listingData.seller?.fullName || 'N/A'}</p>
            <p><strong>Email:</strong> {listingData.seller?.email || 'N/A'}</p>
            <p><strong>Phone:</strong> {listingData.seller?.phoneNumber || 'N/A'}</p>
          </Col>
          <Col md={6}>
            <p><strong>University:</strong> {listingData.seller?.university || 'N/A'}</p>
            <p><strong>Verified:</strong> {listingData.seller?.isVerified ? 'Yes' : 'No'}</p>
            <p><strong>Member Since:</strong> {new Date(listingData.seller?.createdAt).toLocaleDateString()}</p>
          </Col>
        </Row>
      </Card>

      {/* Reports */}
      <Card className="mb-3 p-3">
        <h5 className="mb-3">Reports ({listingData.reports?.length || 0})</h5>
        {listingData.reports && listingData.reports.length > 0 ? (
          <div>
            {listingData.reports.map((report, idx) => (
              <Card key={report.id} className="mb-2 p-3" style={{ backgroundColor: '#fff3cd' }}>
                <Row>
                  <Col md={6}>
                    <p><strong>Report #{idx + 1}</strong></p>
                    <p><strong>Reason:</strong> {report.reasonCode}</p>
                    {report.reasonText && (
                      <p><strong>Details:</strong> {report.reasonText}</p>
                    )}
                    <p><strong>Reported At:</strong> {new Date(report.createdAt).toLocaleString()}</p>
                  </Col>
                  <Col md={6}>
                    <p><strong>Reported By:</strong></p>
                    <p>{report.reporter?.fullName || 'N/A'}</p>
                    <p style={{ fontSize: '12px', color: '#666' }}>
                      {report.reporter?.email || 'N/A'}
                    </p>
                    <p><strong>Status:</strong> 
                      <span style={{ 
                        marginLeft: '8px',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        backgroundColor: report.status === 'PENDING' ? '#ffc107' : '#28a745',
                        color: 'white',
                        fontSize: '11px'
                      }}>
                        {report.status}
                      </span>
                    </p>
                  </Col>
                </Row>
              </Card>
            ))}
          </div>
        ) : (
          <p>No reports found</p>
        )}
      </Card>

      {/* Action Buttons - Only show if there are pending reports and listing is not removed */}
      {(() => {
        const reportStatuses = listingData.reports?.map(r => r.status?.toUpperCase()) || [];
        const isRemoved = listingData.status === 'Removed';
        const hasPendingReports = reportStatuses.some(s => s === 'PENDING');
        const allDismissed = reportStatuses.length > 0 && reportStatuses.every(s => s === 'DISMISSED');
        const allResolved = reportStatuses.length > 0 && reportStatuses.every(s => s === 'RESOLVED');
        
        // Hide buttons if: listing is removed, OR all reports are dismissed/resolved, OR no pending reports
        if (isRemoved || allDismissed || allResolved || !hasPendingReports) {
          return (
            <div className="text-end mt-4">
              <p style={{ color: '#6c757d', fontStyle: 'italic', marginBottom: '10px' }}>
                {isRemoved 
                  ? 'This listing has been removed. No further actions available.'
                  : allDismissed
                  ? 'All reports have been dismissed. No further actions available.'
                  : allResolved
                  ? 'All reports have been resolved. No further actions available.'
                  : 'No pending reports. No actions available.'}
              </p>
            </div>
          );
        }
        
        // Only show buttons if there are pending reports and listing is active
        return (
          <div className="text-end mt-4">
            <Button 
              color="danger" 
              className="me-2"
              onClick={() => {
                setEjectAction('remove');
                setEjectModal(true);
              }}
            >
              Remove Listing
            </Button>
            <Button 
              color="warning"
              onClick={() => {
                setEjectAction('dismiss');
                setEjectModal(true);
              }}
            >
              Dismiss Reports
            </Button>
          </div>
        );
      })()}

      {/* Eject Confirmation Modal */}
      <Modal isOpen={ejectModal} toggle={() => setEjectModal(false)} size="lg">
        <ModalHeader toggle={() => setEjectModal(false)}>
          {ejectAction === 'remove' ? 'Remove Listing' : 'Dismiss Reports'}
        </ModalHeader>
        <ModalBody>
          {ejectAction === 'remove' ? (
            <p>Are you sure you want to remove this listing? This action will mark the listing as "Removed" and it will no longer appear in public listings. All reports will be marked as resolved.</p>
          ) : (
            <p>Are you sure you want to dismiss all reports for this listing? The listing will remain active and reports will be marked as dismissed.</p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button 
            color={ejectAction === 'remove' ? 'danger' : 'warning'} 
            onClick={() => handleEject(ejectAction)}
            disabled={ejecting}
          >
            {ejecting ? 'Processing...' : 'Confirm'}
          </Button>
          <Button color="secondary" onClick={() => setEjectModal(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ReportedListingDetails;
